<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="xlarge">
        <template #header>
          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <Heading level="h2">{{ t('decimator.title') }}</Heading>

            <Tag v-if="decimator.connected" size="small" :color="decimator.has_natebox_defaults ? 'green' : 'orange'">
              {{ decimator.has_natebox_defaults ? t('decimator.natebox_defaults') : t('decimator.custom_settings') }}
            </Tag>
          </div>
        </template>

        <Text v-if="!decimator.connected" class="tw-font-black tw-text-orange">
          {{ t('decimator.not_connected') }}
        </Text>

        <div v-else class="tw-space-y-6">
          <Text class="tw-text-white/68">{{ t('decimator.serial', { serial: decimator.serial }) }}</Text>

          <section class="tw-space-y-3">
            <Heading level="h4">{{ t('decimator.inputs') }}</Heading>

            <div class="tw-grid tw-grid-cols-12 tw-gap-5">
              <Box
                v-for="input in inputs"
                :key="input.label"
                rounded="xlarge"
                class="tw-col-span-full tw-space-y-1 tw-bg-white/5 md:tw-col-span-6"
              >
                <Text size="small" class="tw-text-white/68">{{ input.label }}</Text>
                <Text class="tw-font-black" :class="input.locked ? 'tw-text-state-succ' : 'tw-text-state-err'">
                  {{ input.format }}
                </Text>
              </Box>
            </div>
          </section>

          <SlidesInputPreview v-if="show" />

          <section v-for="section in basicSections" :key="section.id" class="tw-space-y-3">
            <Heading level="h4">{{ t(`decimator.basic.${section.id}`) }}</Heading>

            <DecimatorSettingsGrid
              :settings="section.settings"
              :pending-values="pendingValues"
              :saving="saving"
              :active-scaler-source="activeScalerSource"
              @save="save"
            />
          </section>

          <div class="tw-space-y-6">
            <button
              type="button"
              class="tw-group tw-flex tw-w-full tw-items-center tw-gap-3 tw-text-white/68 hover:tw-text-white"
              :aria-expanded="advancedOpen"
              @click="advancedOpen = !advancedOpen"
            >
              <span class="tw-text-sm tw-font-black tw-uppercase tw-tracking-wider">{{ t('decimator.advanced') }}</span>
              <Icon
                :name="ChevronDownSvg"
                class="tw-size-5 tw-fill-current tw-transition-transform"
                :class="advancedOpen ? 'tw-rotate-180' : ''"
              />
              <span class="tw-h-px tw-flex-1 tw-bg-purple/25 group-hover:tw-bg-white/25" />
            </button>

            <template v-if="advancedOpen">
              <section v-for="group in advancedGroups" :key="group.id" class="tw-space-y-3">
                <Heading level="h4">{{ t(`decimator.groups.${group.id}`) }}</Heading>

                <DecimatorSettingsGrid
                  :settings="group.settings"
                  :pending-values="pendingValues"
                  :saving="saving"
                  @save="save"
                />
              </section>
            </template>
          </div>
        </div>

        <template #footer="{ reject }">
          <div class="tw-flex tw-justify-end tw-gap-2">
            <Button
              color="semiTransparentWhite"
              outlined
              :loading="applyingDefaults"
              :disabled="!decimator.connected || saving"
              @click="applyNateboxDefaults"
            >
              {{ t('decimator.apply_natebox_defaults') }}
            </Button>
            <Button color="transparent" outlined @click="reject">{{ t('decimator.close') }}</Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Button, Heading, Icon, Modal, ModalContent, Tag, Text } from '@slideslive/fuse-kit/vue';

import api from '@/api';
import ChevronDownSvg from '@/assets/mdi/chevron-down.svg';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';

import DecimatorSettingsGrid from './DecimatorSettingsGrid.vue';
import SlidesInputPreview from './SlidesInputPreview.vue';

// Settings an operator needs on site. The rest is under Advanced, grouped as nate-runner sends them.
// The signal goes from the inputs through the scaler to the outputs, so the modal shows them in this order.
const BASIC_SECTIONS = [
  { id: 'scaler', settings: ['DUC_Source', 'DUCFormat'] },
  { id: 'outputs', settings: ['SO_Source', 'HO_Source', 'Loop_Enable'] },
];
const BASIC_SETTINGS = BASIC_SECTIONS.flatMap((section) => section.settings);
const GROUPS = ['output', 'scaler', 'hdmi_audio', 'sdi_audio', 'device'];

const show = defineModel();

const { t } = useI18n();

const stateStore = useStateStore();
const { updateRemoteState } = stateStore;
const { state } = storeToRefs(stateStore);

const decimator = computed(() => state.value?.data?.decimator || { connected: false });

const inputs = computed(() => [
  { label: t('decimator.sdi_input'), ...decimator.value.sdi_input },
  { label: t('decimator.hdmi_input'), ...decimator.value.hdmi_input },
]);

const settings = computed(() => Object.entries(decimator.value.settings || {}));

const basicSections = computed(() =>
  BASIC_SECTIONS.map((section) => ({
    id: section.id,
    settings: section.settings
      .map((name) => settings.value.find(([settingName]) => settingName === name))
      .filter(Boolean),
  })),
);

const advancedGroups = computed(() => {
  const advanced = settings.value.filter(([name]) => !BASIC_SETTINGS.includes(name));

  return GROUPS.map((id) => ({ id, settings: advanced.filter(([, setting]) => setting.group === id) })).filter(
    (group) => group.settings.length,
  );
});

const activeScalerSource = computed(() => {
  const options = decimator.value.settings?.DUC_Source?.options || [];
  return options.find((option) => option.id === decimator.value.active_scaler_source)?.label;
});

// The new value stays visible while it is written and /state is read again.
const pendingValues = ref({});
const saving = ref(false);
const applyingDefaults = ref(false);
const advancedOpen = ref(false);

async function writeAndRefresh(path, payload) {
  saving.value = true;

  try {
    await fetcher.post(path, payload);
    updateRemoteState(await api.state());
  } finally {
    pendingValues.value = {};
    saving.value = false;
  }
}

async function save(name, value) {
  if (saving.value) return;

  pendingValues.value = { [name]: value };
  await writeAndRefresh('/decimator/-/settings', { settings: { [name]: value } });
}

async function applyNateboxDefaults() {
  if (saving.value || !window.confirm(t('decimator.apply_natebox_defaults_confirm'))) return;

  applyingDefaults.value = true;

  try {
    await writeAndRefresh('/decimator/-/apply-natebox-defaults');
  } finally {
    applyingDefaults.value = false;
  }
}
</script>
